/**
 * Conversation / messaging service — domain operations over conversationApi.
 */
import type { AxiosError } from 'axios';
import { ChatContentType, CONVERSATION_TYPE } from '../../constants';
import { isAiConversationType } from '../../constants/conversation';
import { fileUploadApi } from '../fileUpload';
import { extractUploadedFileUrl } from '../fileUpload/fileUploadMapper';
import type {
  Conversation,
  ConversationMember,
  ExtendedChatMessage,
} from '../../types';
import type { ApiError } from '../../types/api';
import conversationApi from '../conversation/conversationApi';
import profileService from './profileService';
import {
  extractConversationList,
  extractListPagination,
  extractSelectableMembers,
  isRemoteMediaUrl,
  mapConversationDto,
  mapMessageDto,
  mapParticipantToMember,
  mapSearchUserToMember,
  parseUnreadCount,
  unwrapSendMessageDto,
} from '../conversation/conversationMapper';
import type {
  ConversationListFilter,
  InitiateConversationRequest,
} from '../conversation/conversationTypes';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';
import { sanitizeParticipantUserIds } from '../conversation/initiateConversationPayload';
import { getFullImageUrl } from '../../utils/imageUrl';
import { getTimezoneOffsetMinutes } from '../../utils/dateHelpers';
import type { UserConnectionItem } from '../../types/userProfile';

export interface ConversationPrefsMap {
  [conversationId: string]: Partial<Conversation>;
}

export interface ConversationPageResult {
  data: Conversation[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface MessagesPageResult {
  messages: ExtendedChatMessage[];
  participants: ConversationMember[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface SendChatMessageInput {
  text?: string;
  replyToMessageId?: number | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  contentTypeId?: number | null;
}

export interface InitiateDirectInput {
  participantUserId: number;
  conversationName?: string | null;
  avatarUrl?: string | null;
}

export interface InitiateGroupInput {
  participantUserIds: number[];
  conversationName?: string | null;
  avatarUrl?: string | null;
}

export interface InitiateRobiAiInput {
  aiAgentUserId: number;
  conversationName?: string | null;
  avatarUrl?: string | null;
}

/** @deprecated Prefer initiateRobiAiConversation / initiateWlbHelpAgentConversation. */
export interface InitiateAiAgentInput {
  conversationType: string;
  aiAgentUserId?: number | null;
  conversationName?: string | null;
  avatarUrl?: string | null;
}

const DEFAULT_PAGE_SIZE = 20;
const FOLLOWING_CANDIDATES_PAGE_SIZE = 100;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const message = getApiErrorMessage(error as ApiError);
  return message || fallback;
};

const getStatusCode = (error: unknown): number | null => {
  const axiosError = error as AxiosError;
  return axiosError?.response?.status ?? null;
};

const mapConnectionToMember = (
  user: UserConnectionItem,
): ConversationMember => {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const avatarPath = user.userPhoto?.trim();
  const avatar =
    avatarPath &&
    (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')
      ? avatarPath
      : getFullImageUrl(avatarPath) || null);

  return {
    id: String(user.userId),
    name: fullName || user.userName?.trim() || `User ${user.userId}`,
    avatar,
  };
};

const fetchConnectionMembers = async ({
  currentUserId,
  relation,
  searchTerm,
  pageSize = FOLLOWING_CANDIDATES_PAGE_SIZE,
}: {
  currentUserId?: number | null;
  relation: string;
  searchTerm?: string;
  pageSize?: number;
}): Promise<ConversationMember[]> => {
  if (!currentUserId) {
    throw new Error('Unable to load following users');
  }

  const response = await profileService.getUserConnections({
    profileUserId: currentUserId,
    relation,
    startIndex: 1,
    pageSize,
    timezoneOffset: getTimezoneOffsetMinutes(),
    searchText: searchTerm?.trim() || undefined,
  });

  return (response?.data?.users ?? [])
    .map(mapConnectionToMember)
    .filter(member => String(member.id) !== String(currentUserId));
};

const messageService = {
  getConversations: async ({
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    searchTerm,
    filter = 'All',
    currentUserId,
    prefs = {},
  }: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    filter?: ConversationListFilter;
    currentUserId?: number | null;
    prefs?: ConversationPrefsMap;
  } = {}): Promise<ConversationPageResult> => {
    try {
      const response =
        searchTerm || filter !== 'All'
          ? await conversationApi.listWithFilter({
              pageNumber: page,
              pageSize,
              searchTerm,
              filter,
            })
          : await conversationApi.list({ pageNumber: page, pageSize });

      let rawList = extractConversationList(response.data);
      const originalLength = rawList.length;

      if (filter === 'All') {
        rawList = rawList.filter(
          dto =>
            dto.conversationType === 'Direct' ||
            dto.conversationType === 'Group'
        );
      }

      const pagination = extractListPagination(
        response.data,
        page,
        pageSize,
        originalLength,
      );

      return {
        data: rawList.map(dto =>
          mapConversationDto(
            dto,
            currentUserId,
            prefs[String(dto.conversationId)],
          ),
        ),
        page,
        pageSize,
        totalCount: pagination.totalCount,
        totalPages: pagination.totalPages,
        hasNextPage: pagination.hasNextPage,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load conversations'));
    }
  },

  getConversationById: async (
    conversationId: string | number,
    currentUserId?: number | null,
    prefs?: Partial<Conversation> | null,
  ): Promise<Conversation> => {
    try {
      const response = await conversationApi.getById(conversationId);
      return mapConversationDto(response.data, currentUserId, prefs);
    } catch (error: any) {
      const errMessage = getErrorMessage(error, 'Failed to load conversation');
      const err = new Error(errMessage);
      const status = getStatusCode(error) || error?.status || error?.response?.status;
      const response = error?.response || (error as any).axiosError?.response;
      if (status) {
        (err as any).status = status;
      }
      if (response) {
        (err as any).response = response;
      }
      // Attach a stable code so callers can detect 404 "Conversation not found"
      // without having to parse error message strings or HTTP status across
      // multiple wrapping layers.
      if (status === 404) {
        (err as any).code = 'CONVERSATION_NOT_FOUND';
      }
      throw err;
    }
  },

  initiateDirectConversation: async (
    input: InitiateDirectInput,
    currentUserId?: number | null,
    prefs?: Partial<Conversation> | null,
  ): Promise<{ conversation: Conversation; created: boolean }> => {
    const participantUserIds = sanitizeParticipantUserIds(
      [input.participantUserId],
      { excludeUserId: currentUserId ?? null },
    );

    if (!participantUserIds.length) {
      throw new Error('Invalid participant user id');
    }

    const payload: InitiateConversationRequest = {
      conversationType: CONVERSATION_TYPE.DIRECT_CHAT,
      participantUserIds,
      conversationName: input.conversationName ?? undefined,
      avatarUrl: input.avatarUrl ?? undefined,
    };

    try {
      const response = await conversationApi.initiate(payload, {
        excludeUserId: currentUserId ?? null,
      });
      return {
        conversation: mapConversationDto(response.data, currentUserId, prefs),
        created: true,
      };
    } catch (error) {
      const status = getStatusCode(error);
      if (status === 409) {
        const conflict = new Error(
          getErrorMessage(error, 'Conversation already exists'),
        ) as Error & { code?: string; status?: number };
        conflict.code = 'CONVERSATION_EXISTS';
        conflict.status = 409;
        throw conflict;
      }
      throw new Error(getErrorMessage(error, 'Failed to start conversation'));
    }
  },

  initiateGroupConversation: async (
    input: InitiateGroupInput,
    currentUserId?: number | null,
  ): Promise<Conversation> => {
    const participantUserIds = sanitizeParticipantUserIds(
      input.participantUserIds,
      { excludeUserId: currentUserId ?? null },
    );

    if (!participantUserIds.length) {
      throw new Error('At least one participant is required');
    }

    try {
      const response = await conversationApi.initiate(
        {
          conversationType: CONVERSATION_TYPE.GROUP_CHAT,
          participantUserIds,
          conversationName: input.conversationName ?? undefined,
          avatarUrl: input.avatarUrl ?? undefined,
        },
        { excludeUserId: currentUserId ?? null },
      );
      return mapConversationDto(response.data, currentUserId);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to create group chat'));
    }
  },

  initiateAiAgentConversation: async (
    input: InitiateAiAgentInput,
  ): Promise<import('../conversation/conversationTypes').ConversationDto> => {
    const agentId = Number(input.aiAgentUserId);
    const hasAgentId = Number.isFinite(agentId) && agentId > 0;

    try {
      const response = await conversationApi.initiate({
        conversationType: input.conversationType,
        participantUserIds: [],
        conversationName: input.conversationName ?? undefined,
        avatarUrl: input.avatarUrl ?? undefined,
        includeAiAgent: true,
        ...(hasAgentId ? { aiAgentUserId: agentId } : {}),
      });
      return response.data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to initiate AI conversation'),
      );
    }
  },

  deleteConversation: async (
    conversationId: string | number,
    leaveConversation = true,
  ): Promise<void> => {
    try {
      await conversationApi.delete({
        conversationId: Number(conversationId),
        leaveConversation,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to delete conversation'));
    }
  },

  /** Block conversation by invoking userRelation Block API against the peer user. */
  blockConversation: async (
    _id?: string,
    peerUserId?: number | string | null,
  ): Promise<{ success: boolean }> => {
    const result = await profileService.setBlockRelation(peerUserId, true);
    if (!result.success) {
      throw new Error(result.message || 'Failed to block conversation');
    }
    return { success: true };
  },

  getMessages: async ({
    conversationId,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    currentUserId,
  }: {
    conversationId: string | number;
    page?: number;
    pageSize?: number;
    currentUserId?: number | null;
  }): Promise<MessagesPageResult> => {
    try {
      const response = await conversationApi.getMessages(conversationId, {
        pageNumber: page,
        pageSize,
        sortDescending: true,
        timezoneOffset: getTimezoneOffsetMinutes(),
      });
      const payload = response.data ?? {};
      const rawMessages = payload.messages ?? [];
      const totalCount = payload.totalCount ?? rawMessages.length;
      const totalPages =
        payload.totalPages ??
        Math.max(1, Math.ceil(totalCount / (payload.pageSize ?? pageSize)));
      const currentPage = payload.pageNumber ?? page;

      return {
        messages: rawMessages.map(m => mapMessageDto(m, currentUserId)),
        participants: (payload.participants ?? []).map(mapParticipantToMember),
        page: currentPage,
        pageSize: payload.pageSize ?? pageSize,
        totalCount,
        totalPages,
        hasNextPage: currentPage < totalPages,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load messages'));
    }
  },

  sendMessage: async ({
    conversationId,
    input,
    currentUserId,
  }: {
    conversationId: string | number;
    input: SendChatMessageInput;
    currentUserId?: number | null;
  }): Promise<ExtendedChatMessage> => {
    // Backend MessageType: 1 Text | 2 Image | 3 Video | 4 File | 5 System | 6 AIResponse
    let finalImageUrl = input.imageUrl as string | undefined;
    let finalVideoUrl = input.videoUrl as string | undefined;

    try {
      if (finalImageUrl && !isRemoteMediaUrl(finalImageUrl)) {
        const res = await fileUploadApi.uploadLocalFile({
          uri: finalImageUrl,
          name: input.fileName || 'upload.jpg',
          type: 'image/jpeg',
          meta: { module: 'Chat', contentType: 'Single' },
        });
        const url = extractUploadedFileUrl(res.data);
        if (url) finalImageUrl = url;
      } else if (finalVideoUrl && !isRemoteMediaUrl(finalVideoUrl)) {
        const res = await fileUploadApi.uploadLocalFile({
          uri: finalVideoUrl,
          name: input.fileName || 'upload.mp4',
          type: 'video/mp4',
          meta: { module: 'Chat', contentType: 'Single' },
        });
        const url = extractUploadedFileUrl(res.data);
        if (url) finalVideoUrl = url;
      }
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to upload media'));
    }

    const hasRemoteImage = isRemoteMediaUrl(finalImageUrl);
    const hasRemoteVideo = isRemoteMediaUrl(finalVideoUrl);
    const messageTypeId = hasRemoteVideo ? ChatContentType.Video : hasRemoteImage ? ChatContentType.Image : ChatContentType.Text;

    // When sending a GIF/image with no text, put the URL in bodyText too.
    // The API may not persist attachments for remote URLs, so bodyText
    // ensures the recipient can recover the image URL on fetch.
    const bodyText = input.text?.trim()
      || (hasRemoteVideo ? (finalVideoUrl as string) : hasRemoteImage ? (finalImageUrl as string) : '');

    try {
      const response = await conversationApi.sendMessage(conversationId, {
        bodyText,
        messageTypeId,
        replyToMessageId: input.replyToMessageId ?? null,
        attachments: (hasRemoteImage || hasRemoteVideo)
          ? [{
              contentTypeId: hasRemoteVideo ? ChatContentType.Video : ChatContentType.Image,
              fileUrl: (hasRemoteVideo ? finalVideoUrl : finalImageUrl) as string,
              fileName: input.fileName ?? '',
              fileSizeBytes: input.fileSizeBytes ?? 0,
              durationSeconds: 0,
            }]
          : [],
      });
      // Human + group SendMessage sometimes returns an envelope / wrapper
      // instead of a bare message DTO — unwrap before mapping.
      const dto = unwrapSendMessageDto(response.data);
      return mapMessageDto(dto ?? ({} as Parameters<typeof mapMessageDto>[0]), currentUserId);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to send message'));
    }
  },

  updateMessage: async ({
    conversationId,
    messageId,
    bodyText,
    currentUserId,
  }: {
    conversationId: string | number;
    messageId: string | number;
    bodyText: string;
    currentUserId?: number | null;
  }): Promise<ExtendedChatMessage> => {
    try {
      const response = await conversationApi.updateMessage(conversationId, {
        messageId: Number(messageId),
        bodyText,
      });
      return mapMessageDto(response.data, currentUserId);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to update message'));
    }
  },

  deleteMessage: async ({
    conversationId,
    messageId,
    deletionReason,
  }: {
    conversationId: string | number;
    messageId: string | number;
    deletionReason?: string;
  }): Promise<void> => {
    try {
      await conversationApi.deleteMessage(conversationId, {
        messageId: Number(messageId),
        deletionReason: deletionReason ?? null,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to delete message'));
    }
  },

  markMessageRead: async (
    conversationId: string | number,
  ): Promise<void> => {
    try {
      await conversationApi.markMessageRead(conversationId);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to mark message as read'));
    }
  },

  getUnreadCount: async (conversationId: string | number): Promise<number> => {
    try {
      const response = await conversationApi.getUnreadCount(conversationId);
      return parseUnreadCount(response.data);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load unread count'));
    }
  },

  getParticipants: async (
    conversationId: string | number,
  ): Promise<ConversationMember[]> => {
    try {
      const response = await conversationApi.getParticipants(conversationId);
      return (response.data ?? []).map(mapParticipantToMember);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load participants'));
    }
  },

  addParticipant: async ({
    conversationId,
    participantUserId,
    isAdmin = false,
  }: {
    conversationId: string | number;
    participantUserId: number;
    isAdmin?: boolean;
  }): Promise<void> => {
    try {
      await conversationApi.addParticipant(conversationId, {
        conversationId: Number(conversationId),
        participantUserId,
        isAdmin,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to add participant'));
    }
  },

  removeParticipant: async ({
    conversationId,
    participantUserId,
  }: {
    conversationId: string | number;
    participantUserId: number;
  }): Promise<void> => {
    try {
      await conversationApi.removeParticipant(conversationId, {
        conversationId: Number(conversationId),
        participantUserId,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to remove participant'));
    }
  },

  /**
   * Ensures a human DirectChat/GroupChat no longer includes the followed AI agent.
   * Backend auto-replies on SendMessage whenever any AI agent is a participant —
   * so we must strip the agent before human messaging.
   */
  ensureNoAiAgentInConversation: async ({
    conversationId,
    aiAgentUserId,
    currentUserId,
  }: {
    conversationId: string | number;
    aiAgentUserId?: number | string | null;
    currentUserId?: number | null;
  }): Promise<{ stripped: boolean; conversationType: string | null }> => {
    const agentId = Number(aiAgentUserId);
    if (!agentId || Number.isNaN(agentId)) {
      return { stripped: false, conversationType: null };
    }

    let conversationType: string | null = null;
    try {
      const conversation = await messageService.getConversationById(
        conversationId,
        currentUserId,
      );
      conversationType = conversation.conversationType
        ? String(conversation.conversationType)
        : null;

      // Never mutate dedicated AI threads from this helper.
      if (isAiConversationType(conversationType)) {
        return { stripped: false, conversationType };
      }
    } catch {
      // Still attempt participant cleanup using the known agent id.
    }

    try {
      const participants = await messageService.getParticipants(conversationId);
      const aiMembers = participants.filter(
        p => Number(p.id) === agentId,
      );
      if (!aiMembers.length) {
        return { stripped: false, conversationType };
      }

      for (const member of aiMembers) {
        try {
          await messageService.removeParticipant({
            conversationId,
            participantUserId: Number(member.id),
          });
        } catch {
          // Best-effort — UI still filters AI messages if removal fails.
        }
      }
      return { stripped: true, conversationType };
    } catch {
      return { stripped: false, conversationType };
    }
  },

  searchUsers: async ({
    searchQuery = '',
    page = 1,
    pageSize = 20,
    onlineOnly = false,
    excludeConversationParticipants = false,
  }: {
    searchQuery?: string;
    page?: number;
    pageSize?: number;
    onlineOnly?: boolean;
    excludeConversationParticipants?: boolean;
  } = {}): Promise<{
    users: ReturnType<typeof mapSearchUserToMember>[];
    totalCount: number;
    hasNextPage: boolean;
    page: number;
  }> => {
    try {
      const response = await conversationApi.searchUsers({
        searchQuery,
        pageNumber: page,
        pageSize,
        onlineOnly,
        excludeConversationParticipants,
      });
      const users = (response.data?.users ?? []).map(mapSearchUserToMember);
      const totalCount = response.data?.totalCount ?? users.length;
      const totalPages =
        response.data?.totalPages ??
        Math.max(1, Math.ceil(totalCount / pageSize));
      return {
        users,
        totalCount,
        hasNextPage: page < totalPages,
        page,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to search users'));
    }
  },

  /**
   * Loads the current user's Following list for conversation participant pickers.
   * Falls back to Conversation/SearchUsers when a search query is provided and
   * Following returns no matches.
   */
  getFollowingCandidates: async ({
    currentUserId,
    searchTerm,
    pageSize = FOLLOWING_CANDIDATES_PAGE_SIZE,
  }: {
    currentUserId?: number | null;
    searchTerm?: string;
    pageSize?: number;
  } = {}): Promise<ConversationMember[]> => {
    try {
      let members = await fetchConnectionMembers({
        currentUserId,
        relation: 'following',
        searchTerm,
        pageSize,
      });

      const query = searchTerm?.trim();
      if (query && members.length === 0) {
        const searchResult = await messageService.searchUsers({
          searchQuery: query,
          page: 1,
          pageSize,
        });
        members = searchResult.users.filter(
          member => String(member.id) !== String(currentUserId),
        );
      }

      return members;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to load following users'),
      );
    }
  },

  /**
   * Loads the current user's Follower list for conversation participant pickers.
   * Falls back to Conversation/SearchUsers when a search query is provided and
   * Followers returns no matches.
   */
  getFollowerCandidates: async ({
    currentUserId,
    searchTerm,
    pageSize = FOLLOWING_CANDIDATES_PAGE_SIZE,
  }: {
    currentUserId?: number | null;
    searchTerm?: string;
    pageSize?: number;
  } = {}): Promise<ConversationMember[]> => {
    try {
      let members = await fetchConnectionMembers({
        currentUserId,
        relation: 'followers',
        searchTerm,
        pageSize,
      });

      const query = searchTerm?.trim();
      if (query && members.length === 0) {
        const searchResult = await messageService.searchUsers({
          searchQuery: query,
          page: 1,
          pageSize,
        });
        members = searchResult.users.filter(
          member => String(member.id) !== String(currentUserId),
        );
      }

      return members;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to load follower users'),
      );
    }
  },

  /**
   * Loads the current user's Buddies list for conversation participant pickers.
   */
  getBuddyCandidates: async ({
    currentUserId,
    searchTerm,
    pageSize = FOLLOWING_CANDIDATES_PAGE_SIZE,
  }: {
    currentUserId?: number | null;
    searchTerm?: string;
    pageSize?: number;
  } = {}): Promise<ConversationMember[]> => {
    try {
      return await fetchConnectionMembers({
        currentUserId,
        relation: 'Buddy',
        searchTerm,
        pageSize,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load buddies'));
    }
  },

  /**
   * Loads Conversation/List and returns unique selectable members
   * (peers / participants excluding the current user).
   * Prefer getFollowingCandidates for new-conversation pickers.
   */
  getMemberCandidates: async ({
    currentUserId,
    searchTerm,
    pageSize = 100,
    prefs = {},
  }: {
    currentUserId?: number | null;
    searchTerm?: string;
    pageSize?: number;
    prefs?: ConversationPrefsMap;
  } = {}): Promise<ConversationMember[]> => {
    try {
      // Prefer Following list for participant selection (client requirement).
      try {
        return await messageService.getFollowingCandidates({
          currentUserId,
          searchTerm,
          pageSize,
        });
      } catch {
        // Fallback to conversation peers when Following is unavailable.
      }

      const page = await messageService.getConversations({
        page: 1,
        pageSize,
        filter: 'All',
        currentUserId,
        prefs,
      });
      return extractSelectableMembers(page.data, currentUserId, searchTerm);
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to load conversation members'),
      );
    }
  },

  getAttachments: async (
    conversationId: string | number,
    messageId: string | number,
  ) => {
    try {
      const response = await conversationApi.getAttachments(
        conversationId,
        messageId,
      );
      return response.data ?? [];
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load attachments'));
    }
  },

  addAttachment: async ({
    conversationId,
    messageId,
    fileUrl,
    fileName,
    fileSizeBytes,
    contentTypeId,
    durationSeconds,
  }: {
    conversationId: string | number;
    messageId: string | number;
    fileUrl: string;
    fileName?: string | null;
    fileSizeBytes?: number | null;
    contentTypeId: number;
    durationSeconds?: number | null;
  }): Promise<void> => {
    try {
      await conversationApi.addAttachment(conversationId, messageId, {
        contentTypeId,
        fileUrl,
        fileName: fileName ?? null,
        fileSizeBytes: fileSizeBytes ?? null,
        durationSeconds: durationSeconds ?? null,
      });
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to add attachment'));
    }
  },
};

export default messageService;
